import { useEffect, useRef } from 'react';

// Minimal typing for the Google Identity Services global (loaded via the GSI script tag).
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (cfg: { client_id: string; callback: (r: { credential: string }) => void }) => void;
          renderButton: (el: HTMLElement, opts: Record<string, unknown>) => void;
        };
      };
    };
  }
}

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;

/**
 * Renders the official Google button and hands the returned ID token to onCredential.
 * If no client ID is configured, it renders nothing — so the app still works on email OTP
 * alone, and Google simply doesn't appear until the Cloud Console setup is done.
 */
export function GoogleButton({ onCredential }: { onCredential: (idToken: string) => void }) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!CLIENT_ID || !ref.current) return;
    let cancelled = false;

    const tryRender = () => {
      if (cancelled) return;
      const g = window.google;
      if (!g || !ref.current) { setTimeout(tryRender, 200); return; } // wait for the GSI script to load
      g.accounts.id.initialize({
        client_id: CLIENT_ID,
        callback: (r) => onCredential(r.credential),
      });
      g.accounts.id.renderButton(ref.current, {
        theme: 'outline', size: 'large', width: 320, text: 'continue_with', shape: 'rectangular',
      });
    };
    tryRender();
    return () => { cancelled = true; };
  }, [onCredential]);

  if (!CLIENT_ID) return null;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
        <div style={{ flex: 1, height: 1, background: 'var(--line)' }} />
        <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>or</span>
        <div style={{ flex: 1, height: 1, background: 'var(--line)' }} />
      </div>
      <div ref={ref} style={{ display: 'flex', justifyContent: 'center' }} />
    </div>
  );
}
